import React from 'react';
import { useParams } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import type { PaginationProps, TableCellHeaderSortOptions } from 'app/components';
import { ConfirmDialog, useBreadcrumb } from 'app/components';
import { TranslationKeys } from 'app/translations';
import {
  useConnectionApproveDraftMutation,
  useConnectionArchiveEventsFromDraftMutation,
  useConnectionGetBatchCompareDataQuery,
  useConnectionMarkAsCompleteMutation,
  useDialog,
  useProcessDeclineMutation,
  useSnakeBar,
} from 'app/hooks';
import { CompareBatchPageContent } from './components';
import { useNavigate } from 'react-router-dom';
import { useConnectionResendMutationsMutation } from '../../../../hooks/api/connection/useConnectionResendMutationsMutation';
import { useConnectionDeclineMutationsMutation } from '../../../../hooks/api/connection/useConnectionDeclineMutationsMutation';
import { useCompareBatchPolling } from './hooks';
import { actions as ConnectionActions, selectors as connectionSelectors } from '../../Ducks/Connection.duck';

type Filters = {
  searchQuery: string;
  mutationType: string;
  page: PaginationProps['page'];
  rowsPerPage: PaginationProps['rowsPerPage'];
  sortBy: Record<string, TableCellHeaderSortOptions>;
};

type FiltersMap = Map<keyof Filters, Filters[keyof Filters]>;

type CompareBatchPageParams = {
  employerId: string;
  connectionId: string;
  batchId: string;
};

export const APPROVE_BATCH_DIALOG_DATA_TESTID = 'approve-batch';
export const ARCHIVE_BATCH_DIALOG_DATA_TESTID = 'archive-batch';

export const CompareBatchPage = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { connectionId, batchId, employerId } = useParams<CompareBatchPageParams>();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();
  const { mutate: approveDraftBatchMutation } = useConnectionApproveDraftMutation();
  const { mutate: archiveFromDraftBatchMutation } = useConnectionArchiveEventsFromDraftMutation();
  const { mutate: markAsComplete } = useConnectionMarkAsCompleteMutation();
  const { mutate: resendMutations } = useConnectionResendMutationsMutation();
  const { mutate: declineMutations } = useConnectionDeclineMutationsMutation();
  const { mutate: processDeclineMutation } = useProcessDeclineMutation();

  const { items: mutationTypes } = useSelector((state: any) =>
    connectionSelectors.mutationTypes(state.ConnectionReducer),
  ) as { items: string[] };

  React.useEffect(() => {
    if (connectionId) {
      dispatch(ConnectionActions.mutationTypes.requestData({ connectionId }));
    }
  }, [connectionId, dispatch]);

  const {
    dialogState: showConfirmApproveDialog,
    openDialog: openConfirmApproveDialog,
    closeDialog: closeConfirmApproveDialog,
  } = useDialog();

  const {
    dialogState: showConfirmArchiveDialog,
    openDialog: openConfirmArchiveDialog,
    closeDialog: closeConfirmArchiveDialog,
  } = useDialog();

  const taskId = undefined;
  const [selected, setSelected] = React.useState<Set<string>>(new Set<string>([]));

  const [filters, setFilters] = React.useState<FiltersMap>(() => {
    const defaultState = new Map<keyof Filters, Filters[keyof Filters]>();
    defaultState.set('searchQuery', '');
    defaultState.set('mutationType', '');
    defaultState.set('page', 1);
    defaultState.set('rowsPerPage', 10);
    defaultState.set('sortBy', {});
    return defaultState;
  });

  const searchQuery = filters.get('searchQuery') as string;
  const page = filters.get('page') as number;
  const rowsPerPage = filters.get('rowsPerPage') as number;
  const mutationType = filters.get('mutationType') as string;
  const sortBy = filters.get('sortBy') as Record<string, TableCellHeaderSortOptions>;

  const {
    data: batchCompareData,
    isLoading: isGetBatchCompareDataQueryLoading,
    isFetching: isGetBatchCompareDataQueryFetching,
    refetch: refetchGetBatchCompareDataQuery,
    error: getBatchCompareDataQueryError,
  } = useConnectionGetBatchCompareDataQuery({
    variables: {
      batchId,
      connectionId,
      searchQuery,
      mutationType,
      page,
      pageSize: rowsPerPage,
      sortBy,
    },
    options: {
      enabled: !!batchId && !!connectionId,
    },
  });

  const { isSynchronizing, startPolling } = useCompareBatchPolling({
    refetchGetBatchCompareDataQuery,
  });

  const goBackToConnectionInfo = React.useCallback(() => {
    navigate(`/employers/${employerId}/connections/${connectionId}/info`, { replace: true });
  }, [navigate, employerId, connectionId]);

  React.useEffect(() => {
    if (getBatchCompareDataQueryError?.response?.status === 404) {
      goBackToConnectionInfo();
    }
  }, [getBatchCompareDataQueryError, goBackToConnectionInfo]);

  const onConfirmApproveBatch = React.useCallback(() => {
    if (batchId && connectionId && selected.size > 0) {
      const method = 'approveDraftBatch';

      approveDraftBatchMutation(
        {
          batchId,
          connectionId,
          externalIds: Array.from(selected),
        },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method,
              message: intl.formatMessage({ id: TranslationKeys.connection_processes_compareBatch_successApprovedMsg }),
            });
            refetchGetBatchCompareDataQuery();
          },
          onError: error => {
            // eslint-disable-next-line no-console
            console.error(error);
            showErrorSnakeBar({ method, message: intl.formatMessage({ id: TranslationKeys.global_errorMsg }) });
          },
        },
      );
    }
  }, [
    approveDraftBatchMutation,
    batchId,
    connectionId,
    intl,
    selected,
    showErrorSnakeBar,
    showSuccessSnakeBar,
    refetchGetBatchCompareDataQuery,
  ]);

  const onConfirmArchiveBatch = React.useCallback(() => {
    if (batchId && connectionId && selected.size > 0) {
      const method = 'archiveDraftBatch';

      archiveFromDraftBatchMutation(
        {
          batchId,
          connectionId,
          externalIds: Array.from(selected),
        },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method,
              message: intl.formatMessage({ id: TranslationKeys.connection_processes_compareBatch_successArchivedMsg }),
            });
            refetchGetBatchCompareDataQuery();
          },
          onError: error => {
            // eslint-disable-next-line no-console
            console.error(error);
            showErrorSnakeBar({ method, message: intl.formatMessage({ id: TranslationKeys.global_errorMsg }) });
          },
        },
      );
    }
  }, [
    archiveFromDraftBatchMutation,
    batchId,
    connectionId,
    intl,
    selected,
    showErrorSnakeBar,
    showSuccessSnakeBar,
    refetchGetBatchCompareDataQuery,
  ]);

  const onConfirmMarkAsCompleted = React.useCallback(() => {
    if (batchId && connectionId) {
      const method = 'markBatchAsCompleted';

      markAsComplete(
        { batchId, connectionId },
        {
          onSuccess: () => {
            goBackToConnectionInfo();
          },
          onError: error => {
            // eslint-disable-next-line no-console
            console.error(error);
            showErrorSnakeBar({ method, message: intl.formatMessage({ id: TranslationKeys.global_errorMsg }) });
          },
        },
      );
    }
  }, [markAsComplete, batchId, connectionId, intl, showErrorSnakeBar, goBackToConnectionInfo]);

  const onConfirmMarkSelectedAsCompleted = React.useCallback(() => {
    if (batchId && connectionId && selected.size > 0) {
      const method = 'markSelectedAsCompleted';

      markAsComplete(
        { batchId, connectionId, externalIds: Array.from(selected) },
        {
          onSuccess: () => {
            refetchGetBatchCompareDataQuery();
          },
          onError: error => {
            // eslint-disable-next-line no-console
            console.error(error);
            showErrorSnakeBar({ method, message: intl.formatMessage({ id: TranslationKeys.global_errorMsg }) });
          },
        },
      );
    }
  }, [markAsComplete, batchId, connectionId, intl, selected, showErrorSnakeBar, refetchGetBatchCompareDataQuery]);

  const onConfirmResendMutations = React.useCallback(() => {
    if (batchId && connectionId) {
      const method = 'resendMutations';

      resendMutations(
        {
          batchId,
          connectionId,
          externalIds: Array.from(selected),
        },
        {
          onSuccess: () => {
            /* markAsComplete(
              { batchId, connectionId },
              {
                onSuccess: () => {
                  refetchGetBatchCompareDataQuery();
                },
                onError: error => {
                  // eslint-disable-next-line no-console
                  console.error(error);
                  showErrorSnakeBar({ method, message: intl.formatMessage({ id: TranslationKeys.global_errorMsg }) });
                },
              },
            ); */
            refetchGetBatchCompareDataQuery();
          },
          onError: error => {
            // eslint-disable-next-line no-console
            console.error(error);
            showErrorSnakeBar({ method, message: intl.formatMessage({ id: TranslationKeys.global_errorMsg }) });
          },
        },
      );
    }
  }, [resendMutations, batchId, connectionId, intl, selected, showErrorSnakeBar, refetchGetBatchCompareDataQuery]);

  const onConfirmRejectMutations = React.useCallback(() => {
    if (batchId && connectionId) {
      const method = 'rejectMutations';

      declineMutations(
        {
          batchId,
          connectionId,
          externalIds: Array.from(selected),
        },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method,
              message: intl.formatMessage({ id: TranslationKeys.connection_processes_compareBatch_rejectMutationsMsg }),
            });
            selected.clear();
            refetchGetBatchCompareDataQuery();
          },
          onError: error => {
            // eslint-disable-next-line no-console
            console.error(error);
            showErrorSnakeBar({ method, message: intl.formatMessage({ id: TranslationKeys.global_errorMsg }) });
          },
        },
      );
    }
  }, [
    batchId,
    connectionId,
    declineMutations,
    selected,
    showSuccessSnakeBar,
    intl,
    refetchGetBatchCompareDataQuery,
    showErrorSnakeBar,
  ]);

  const onConfirmRejectAllMutations = React.useCallback(() => {
    if (batchId && connectionId) {
      const method = 'declineProcess';

      processDeclineMutation(
        {
          connectionId,
          batchId,
        },
        {
          onSuccess: () => {
            navigate(`employers/${employerId}/connections/${connectionId}/events`, { replace: true });
            showSuccessSnakeBar({ method });
          },
          onError: () => {
            showErrorSnakeBar({ method });
          },
        },
      );
    }
  }, [batchId, connectionId, employerId, navigate, processDeclineMutation, showErrorSnakeBar, showSuccessSnakeBar]);

  const startListeningForDataAfterUploadFile = React.useCallback(() => {
    refetchGetBatchCompareDataQuery();
  }, [refetchGetBatchCompareDataQuery]);

  const onSearch = React.useCallback(
    (inputValue: string) => {
      filters.set('searchQuery', inputValue);
      filters.set('page', 1);

      setFilters(new Map(filters));
    },
    [filters],
  );

  const onFilterMutationType = React.useCallback(
    (filter: string) => {
      filters.set('mutationType', filter);
      filters.set('page', 1);
      setFilters(new Map(filters));
    },
    [filters],
  );

  const onSortChange = React.useCallback(
    (newSortBy: Record<string, TableCellHeaderSortOptions>) => {
      filters.set('sortBy', newSortBy);
      filters.set('page', 1);
      setFilters(new Map(filters));
    },
    [filters],
  );

  const onSelect = React.useCallback((newSelected: Set<string>) => {
    setSelected(new Set(newSelected));
  }, []);

  const onPaginationChange = React.useCallback<NonNullable<PaginationProps['onPaginationChange']>>(
    ({ page, rowSize }) => {
      if (page !== undefined) {
        filters.set('page', page);
      }
      if (rowSize !== undefined) {
        filters.set('rowsPerPage', rowSize);
        filters.set('page', 1);
      }

      setFilters(new Map(filters));
    },
    [filters],
  );

  const paginationProps = React.useMemo<
    Pick<PaginationProps, 'page' | 'rowsPerPage' | 'count' | 'onPaginationChange'>
  >(() => {
    return {
      page,
      rowsPerPage,
      onPaginationChange,
      count: batchCompareData?.totalCount,
    };
  }, [batchCompareData?.totalCount, onPaginationChange, page, rowsPerPage]);

  useBreadcrumb(TranslationKeys.connection_processes_compareBatch);

  const isLoadingGetBatchCompareDataQuery = isGetBatchCompareDataQueryLoading || isGetBatchCompareDataQueryFetching;

  return (
    <>
      <CompareBatchPageContent
        isLoading={isLoadingGetBatchCompareDataQuery}
        waitingForData={!!taskId}
        connectionId={connectionId}
        batchId={batchId}
        batchCompareData={batchCompareData?.data || []}
        selected={selected}
        onSelect={onSelect}
        onSearch={onSearch}
        onFilterMutationType={onFilterMutationType}
        onSortChange={onSortChange}
        sortBy={sortBy}
        searchQuery={searchQuery}
        startListeningForDataAfterUploadFile={startListeningForDataAfterUploadFile}
        approveBatch={openConfirmApproveDialog}
        archiveBatch={openConfirmArchiveDialog}
        markAsCompleted={onConfirmMarkAsCompleted}
        markSelectedAsCompleted={onConfirmMarkSelectedAsCompleted}
        resendMutations={onConfirmResendMutations}
        rejectMutations={onConfirmRejectMutations}
        declineBatch={onConfirmRejectAllMutations}
        paginationProps={paginationProps}
        startPolling={startPolling}
        isSynchronizing={isSynchronizing}
        mutationTypes={mutationTypes || []}
      />

      {showConfirmApproveDialog && (
        <ConfirmDialog
          title={<FormattedMessage id={TranslationKeys.connection_processes_compareBatch_approveConfirmDialogTitle} />}
          open={showConfirmApproveDialog}
          onClose={closeConfirmApproveDialog}
          onConfirm={onConfirmApproveBatch}
          testId={APPROVE_BATCH_DIALOG_DATA_TESTID}
        >
          <FormattedMessage id={TranslationKeys.connection_processes_compareBatch_approveConfirmDialogBody} />
        </ConfirmDialog>
      )}
      {showConfirmArchiveDialog && (
        <ConfirmDialog
          title={<FormattedMessage id={TranslationKeys.connection_processes_compareBatch_approveConfirmDialogTitle} />}
          open={showConfirmArchiveDialog}
          onClose={closeConfirmArchiveDialog}
          onConfirm={onConfirmArchiveBatch}
          testId={ARCHIVE_BATCH_DIALOG_DATA_TESTID}
        >
          <FormattedMessage id={TranslationKeys.connection_processes_compareBatch_approveConfirmDialogBody} />
        </ConfirmDialog>
      )}
    </>
  );
};
